import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Header } from "@/components/Header";
import { QRScanner } from "@/components/QRScanner";
import { PinInput } from "@/components/PinInput";
import { CurrencyPicker } from "@/components/CurrencyPicker";
import { storage } from "@/lib/storage";
import { getCurrency, formatMoney } from "@/lib/currencies";
import { Check, QrCode, ShieldCheck, Store } from "lucide-react";

export default function CustomerPay() {
  const navigate = useNavigate();
  const [stage, setStage] = useState("scan"); // scan | manual | confirm | pin | done
  const [payMethod, setPayMethod] = useState("qr");
  const [parsed, setParsed] = useState(null);
  const [manualAmount, setManualAmount] = useState("");
  const [manualMid, setManualMid] = useState("");
  const [currency, setCurrency] = useState(storage.getCurrency());
  const [pinErr, setPinErr] = useState("");

  const onDecode = (text) => {
    setPayMethod("qr");
    try {
      const data = JSON.parse(text);
      if (data && data.type === "offline_pay") {
        setParsed(data);
        setStage("confirm");
        return;
      }
    } catch (e) {}
    // treat as short code
    setParsed({ mId: text.slice(0, 8), mName: "Merchant", amt: 0, cur: currency });
    setStage("confirm");
  };

  const goManual = () => {
    setPayMethod("manual");
    setStage("manual");
  };

  const submitManual = () => {
    const amt = Number(manualAmount);
    if (!amt || !manualMid) return;
    setPayMethod("manual");
    setParsed({ mId: manualMid, mName: "Merchant", amt, cur: currency, note: "Manual pay" });
    setStage("confirm");
  };

  const confirmPay = () => {
    setStage("pin");
  };

  const verifyPin = (pin) => {
    if (!storage.verifyPin(pin)) {
      setPinErr("Wrong PIN. Try again.");
      return;
    }
    storage.addTransaction({
      direction: "sent",
      amount: Number(parsed.amt || manualAmount),
      currency: parsed.cur || currency,
      method: payMethod,
      merchantId: parsed.mId,
      merchantName: parsed.mName,
      note: parsed.note,
    });
    setStage("done");
    setTimeout(() => navigate("/history"), 1500);
  };

  return (
    <div className="page-shell" data-testid="customer-pay-page">
      <Header
        title="Pay a Merchant"
        subtitle="Scan QR or enter code · fully offline"
        right={stage === "scan" || stage === "manual" ? <CurrencyPicker value={currency} onChange={setCurrency} compact /> : null}
      />

      {stage === "scan" && (
        <div className="card-glass rounded-2xl p-6 fade-slide-up">
          <QRScanner onDecode={onDecode} onManual={goManual} />
        </div>
      )}

      {stage === "manual" && (
        <div className="card-glass gold-glow rounded-2xl p-6 fade-slide-up">
          <h3 className="font-semibold text-[#F7E6B4] mb-4 flex items-center gap-2">
            <QrCode size={16} className="text-[#E6B455]" /> Manual payment entry
          </h3>
          <input
            type="text"
            placeholder="Merchant ID (e.g. M123456)"
            value={manualMid}
            onChange={(e) => setManualMid(e.target.value)}
            className="w-full bg-[rgba(16,22,42,0.6)] border gold-border rounded-xl px-4 py-3 text-sm text-[#F7E6B4] outline-none placeholder:text-[#F7E6B4]/35 mb-3"
            data-testid="pay-manual-mid"
          />
          <div className="flex items-center gap-3 mb-4">
            <span className="text-2xl font-bold text-[#E6B455]">{getCurrency(currency).symbol}</span>
            <input
              type="number"
              inputMode="decimal"
              placeholder="0.00"
              value={manualAmount}
              onChange={(e) => setManualAmount(e.target.value)}
              className="flex-1 bg-transparent text-4xl font-bold text-[#F7E6B4] outline-none placeholder:text-[#F7E6B4]/20 min-w-0"
              data-testid="pay-manual-amount"
            />
          </div>
          <button
            onClick={submitManual}
            disabled={!(Number(manualAmount) > 0 && manualMid)}
            className="btn-gold w-full py-3 rounded-xl font-semibold disabled:opacity-40"
            data-testid="pay-manual-submit"
          >
            Continue
          </button>
          <button
            onClick={() => setStage("scan")}
            className="w-full mt-3 text-xs text-[#E6B455] hover:underline"
          >
            ← Back to scanner
          </button>
        </div>
      )}

      {stage === "confirm" && parsed && (
        <div className="card-glass gold-glow rounded-2xl p-6 fade-slide-up" data-testid="pay-confirm">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-12 h-12 rounded-xl bg-[rgba(230,180,85,0.1)] flex items-center justify-center">
              <Store size={22} className="text-[#E6B455]" />
            </div>
            <div>
              <div className="text-xs text-[#F7E6B4]/60 uppercase tracking-wider">Paying to</div>
              <div className="font-semibold text-[#F7E6B4]" data-testid="pay-merchant-name">
                {parsed.mName || "Merchant"} · {parsed.mId}
              </div>
            </div>
          </div>

          <div className="text-center py-6 border-y border-[rgba(230,180,85,0.14)]">
            <div className="text-[11px] uppercase tracking-wider text-[#F7E6B4]/55">Amount</div>
            {Number(parsed.amt) > 0 ? (
              <div className="text-5xl font-bold gold-text mt-1" data-testid="pay-amount-display">
                {formatMoney(parsed.amt, parsed.cur || currency)}
              </div>
            ) : (
              <div className="mt-2 flex items-center justify-center gap-2">
                <span className="text-2xl font-bold text-[#E6B455]">{getCurrency(currency).symbol}</span>
                <input
                  type="number"
                  inputMode="decimal"
                  placeholder="Enter amount"
                  value={manualAmount}
                  onChange={(e) => {
                    setManualAmount(e.target.value);
                    setParsed({ ...parsed, amt: Number(e.target.value) || 0, cur: currency });
                  }}
                  className="bg-transparent text-3xl font-bold text-[#F7E6B4] outline-none placeholder:text-[#F7E6B4]/25 text-center w-48"
                  data-testid="pay-amount-input"
                  autoFocus
                />
              </div>
            )}
            {parsed.note && (
              <div className="text-xs text-[#F7E6B4]/55 mt-2">"{parsed.note}"</div>
            )}
          </div>

          <button
            onClick={confirmPay}
            disabled={!(Number(parsed.amt) > 0)}
            className="btn-gold w-full py-3.5 rounded-xl mt-6 font-semibold disabled:opacity-40 flex items-center justify-center gap-2"
            data-testid="pay-confirm-btn"
          >
            <ShieldCheck size={16} /> Pay with PIN
          </button>
          <button
            onClick={() => setStage("scan")}
            className="w-full mt-3 text-xs text-[#F7E6B4]/60 hover:text-[#F7E6B4]"
            data-testid="pay-cancel-btn"
          >
            Cancel
          </button>
        </div>
      )}

      {stage === "pin" && (
        <div className="flex flex-col items-center mt-6 fade-slide-up">
          <PinInput onComplete={verifyPin} label="Enter PIN to authorise payment" error={pinErr} />
          <button
            onClick={() => setStage("confirm")}
            className="mt-6 text-xs text-[#F7E6B4]/60 hover:text-[#F7E6B4]"
          >
            ← Back
          </button>
        </div>
      )}

      {stage === "done" && parsed && (
        <div className="card-glass gold-glow rounded-3xl p-8 flex flex-col items-center text-center fade-slide-up" data-testid="pay-done">
          <div className="w-20 h-20 rounded-full bg-[rgba(230,180,85,0.15)] flex items-center justify-center mb-5">
            <Check size={44} className="text-[#E6B455]" />
          </div>
          <h2 className="text-2xl font-bold gold-text mb-2">Payment sent</h2>
          <div className="text-3xl font-bold text-[#F7E6B4]" data-testid="pay-done-amount">
            {formatMoney(parsed.amt, parsed.cur || currency)}
          </div>
          <div className="text-sm text-[#F7E6B4]/60 mt-1">to {parsed.mName || "Merchant"} · {parsed.mId}</div>
        </div>
      )}
    </div>
  );
}
